import React, { Suspense, lazy, useState, useEffect as useEffectDefer } from "react";
import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import Layout from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";

// Defer non-critical UI widgets. These all rendered at the root on every
// page in the eager bundle before, parsing + executing on the main thread
// before the page became interactive. Lighthouse penalizes that heavily
// on the simulated Moto G4 CPU. Now they only load after the page is idle.
const SupportChat = lazy(() => import("@/components/SupportChat"));
const CookieBanner = lazy(() => import("@/components/CookieBanner"));
const InstallPwaPrompt = lazy(() => import("@/components/InstallPwaPrompt"));

/**
 * Mount lazily-loaded non-critical widgets only AFTER the first paint
 * + a short idle delay. This protects LCP and TBT on the simulated
 * slow CPU that Lighthouse uses.
 */
function DeferredWidgets() {
    const [mount, setMount] = useState(false);
    useEffectDefer(() => {
        // requestIdleCallback for browsers that support it; setTimeout fallback.
        const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
        const handle = ric(() => setMount(true), { timeout: 2500 });
        return () => {
            if (window.cancelIdleCallback) window.cancelIdleCallback(handle);
            else clearTimeout(handle);
        };
    }, []);
    if (!mount) return null;
    return (
        <Suspense fallback={null}>
            <CookieBanner />
            <InstallPwaPrompt />
            <SupportChat />
        </Suspense>
    );
}

function PageViewTracker() {
    const loc = useLocation();
    const lastPathRef = React.useRef(null);
    useEffect(() => {
        // Dedupe StrictMode double-mount + ignore consecutive identical pathnames
        if (lastPathRef.current === loc.pathname) return;
        lastPathRef.current = loc.pathname;
        // Defer to idle so analytics never competes with LCP/TBT/TTI.
        // Lighthouse on simulated 4× CPU specifically penalizes any work in
        // the first ~3s after navigation start.
        const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
        ric(() => trackPageView(loc.pathname), { timeout: 4000 });
    }, [loc.pathname]);
    return null;
}

// Dispatcher: `/jacksonville/:slug` ends with `-rewards` → BusinessPage,
// ends with `-deals` → LocalDealsPage (category mode). Lets us co-locate
// both SEO URL patterns under the same React Router rule.
function JacksonvilleDispatcher() {
    const { slug } = useParams();
    if ((slug || "").endsWith("-deals")) return <LocalDealsPage mode="category" />;
    return <BusinessPage />;
}

// Code-splitting: eager-load Landing only (LCP-critical first paint), lazy-load
// every other route. Cuts initial JS payload by ~700 KB per Lighthouse audit.
import Landing from "@/pages/Landing";
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const MemberDashboard = lazy(() => import("@/pages/MemberDashboard"));
const VideosPage = lazy(() => import("@/pages/Videos"));
const OffersPage = lazy(() => import("@/pages/Offers"));
const MarketplacePage = lazy(() => import("@/pages/Marketplace"));
const AdminPage = lazy(() => import("@/pages/Admin"));
const PartnersPage = lazy(() => import("@/pages/Partners"));
const BusinessDashboard = lazy(() => import("@/pages/BusinessDashboard"));
const LegalPage = lazy(() => import("@/pages/Legal"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const DeveloperDashboard = lazy(() => import("@/pages/DeveloperDashboard"));
const YouthTerms = lazy(() => import("@/pages/YouthTerms"));
const VerifyParental = lazy(() => import("@/pages/VerifyParental"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const BusinessPage = lazy(() => import("@/pages/BusinessPage"));
const LocalDealsPage = lazy(() => import("@/pages/LocalDealsPage"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const DriveLaunchPage = lazy(() => import("@/pages/DriveLaunchPage"));
const AboutPage = lazy(() => import("@/pages/About"));

import "@/App.css";

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <ScrollToTop />
                <PageViewTracker />
                <Layout>
                    <Suspense fallback={
                        <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
                            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                        </div>
                    }>
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/join" element={<Register />} />
                            <Route path="/videos" element={<VideosPage />} />
                            {/* /videos/submit route retired — pre-launch product decision */}
                            <Route path="/videos/submit" element={<Navigate to="/videos" replace />} />
                            <Route path="/offers" element={<OffersPage />} />
                            <Route path="/drive" element={<DriveLaunchPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/marketplace" element={<MarketplacePage />} />
                            <Route path="/partners" element={<PartnersPage />} />
                            <Route path="/business" element={<ProtectedRoute><BusinessDashboard /></ProtectedRoute>} />
                            <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
                            <Route path="/dashboard" element={<ProtectedRoute><MemberDashboard /></ProtectedRoute>} />
                            <Route path="/developer" element={<ProtectedRoute><DeveloperDashboard /></ProtectedRoute>} />
                            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
                            <Route path="/terms" element={<LegalPage doc="terms" />} />
                            <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                            <Route path="/voucher-terms" element={<LegalPage doc="voucher" />} />
                            <Route path="/video-terms" element={<LegalPage doc="video" />} />
                            <Route path="/referral-terms" element={<LegalPage doc="referral" />} />
                            <Route path="/founding-business-terms" element={<LegalPage doc="founding_business" />} />
                            <Route path="/developer-terms" element={<LegalPage doc="developer" />} />
                            <Route path="/ambassador-terms" element={<LegalPage doc="ambassador" />} />
                            <Route path="/youth-terms" element={<YouthTerms />} />
                            <Route path="/verify-parental/:token" element={<VerifyParental />} />
                            <Route path="/settings" element={<SettingsPage />} />
                            <Route path="/jacksonville/:slug" element={<JacksonvilleDispatcher />} />
                            <Route path="/jacksonville/neighborhoods/:slug" element={<LocalDealsPage mode="neighborhood" />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                </Layout>
                <DeferredWidgets />
                <Toaster richColors position="top-right" />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
